import { motion, AnimatePresence } from 'framer-motion';
import { Logo } from './Logo';
import { useTranslation } from 'react-i18next';

export type SocialStatus = 'processing' | 'success' | 'error';

interface SocialLoginStatusProps {
    status: SocialStatus;
    provider?: string;
    errorMessage?: string;
    variant?: 'overlay' | 'page';
}

export const SocialLoginStatus = ({ 
    status, 
    provider = 'Google', 
    errorMessage,
    variant = 'overlay'
}: SocialLoginStatusProps) => {
    const { t } = useTranslation('common');

    const isProcessing = status === 'processing';
    const isSuccess = status === 'success';
    const isError = status === 'error';

    const CDN_URL = ((window as any).__DASHBAL_CONFIG__?.cdnUrl || 'https://cdn.dashbal.com').replace(/\/$/, '');
    
    const getProviderIcon = (p: string) => {
        const lower = p.toLowerCase();
        if (lower.includes('meta') || lower.includes('facebook') || lower.includes('instagram')) 
            return `${CDN_URL}/logos/channel_logos/meta/meta_primary.png`;
        if (lower.includes('tiktok')) 
            return `${CDN_URL}/logos/channel_logos/tiktok/tiktok_primary.png`;
        if (lower.includes('shopify')) 
            return `${CDN_URL}/logos/channel_logos/shopify/shopify_primary.png`;
        if (lower.includes('woocommerce')) 
            return `${CDN_URL}/logos/channel_logos/woocommerce/Woo_primary.png`;
        if (lower.includes('google')) 
            return `${CDN_URL}/logos/channel_logos/google/google_primary.png`;
        return null;
    };

    const providerIcon = getProviderIcon(provider);

    const containerClasses = variant === 'page'
        ? "w-full flex items-center justify-center"
        : "absolute inset-0 z-50 flex items-center justify-center bg-white/90 dark:bg-neutral-900/95 backdrop-blur-sm rounded-xl overflow-hidden";

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className={containerClasses}
        >
            <div className="flex flex-col items-center text-center px-4 max-w-sm">
                <div className="relative w-20 h-20 mb-8">
                    {/* Pulsing background glow */}
                    <motion.div
                        animate={isSuccess ? { scale: [1, 1.25, 1], opacity: [0.3, 0.6, 0.3] } : { scale: [1, 1.15, 1], opacity: [0.3, 0.5, 0.3] }}
                        transition={{ duration: isSuccess ? 1 : 2, repeat: Infinity, ease: 'easeInOut' }}
                        className={`absolute inset-0 ${isSuccess ? 'bg-green-500/20' : isError ? 'bg-red-500/20' : 'bg-accent-primary/20'} rounded-full blur-2xl`}
                    />

                    <AnimatePresence mode="wait">
                        {isProcessing ? (
                            <motion.div
                                key="processing"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                className="absolute inset-0 flex items-center justify-center"
                            >
                                {/* Spinning ring */}
                                <motion.div
                                    animate={{ rotate: 360 }}
                                    transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
                                    className="absolute inset-0 m-auto w-20 h-20 rounded-full border-2 border-transparent border-t-accent-primary border-r-accent-primary/30"
                                />
                                
                                <motion.div 
                                    animate={{ scale: [1, 1.05, 1] }}
                                    transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
                                    className="relative z-10 p-4"
                                >
                                    {providerIcon ? (
                                        <img src={providerIcon} alt={provider} className="w-10 h-10 object-contain drop-shadow-sm" />
                                    ) : (
                                        <Logo className="w-10 h-10" />
                                    )}
                                </motion.div>
                            </motion.div>
                        ) : isSuccess ? (
                            <motion.div
                                key="success"
                                initial={{ scale: 0, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{ type: "spring", stiffness: 260, damping: 20 }}
                                className="absolute inset-0 m-auto w-20 h-20 rounded-full border-2 border-green-500 flex items-center justify-center bg-green-50 dark:bg-green-900/20 shadow-lg shadow-green-500/10"
                            >
                                <svg className="w-10 h-10 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                    <motion.path
                                        initial={{ pathLength: 0 }}
                                        animate={{ pathLength: 1 }}
                                        transition={{ duration: 0.4, delay: 0.2 }}
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={3}
                                        d="M5 13l4 4L19 7"
                                    />
                                </svg>
                            </motion.div>
                        ) : (
                            <motion.div
                                key="error"
                                initial={{ scale: 0, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{ type: "spring", stiffness: 260, damping: 20 }}
                                className="absolute inset-0 m-auto w-20 h-20 rounded-full border-2 border-red-500 flex items-center justify-center bg-red-50 dark:bg-red-900/20 shadow-lg shadow-red-500/10"
                            >
                                <svg className="w-10 h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                    <motion.path
                                        initial={{ pathLength: 0 }}
                                        animate={{ pathLength: 1 }}
                                        transition={{ duration: 0.4, delay: 0.2 }}
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={3}
                                        d="M6 18L18 6M6 6l12 12"
                                    />
                                </svg>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

                <div className="space-y-3">
                    <h3 className="text-xl font-bold text-neutral-900 dark:text-neutral-100 tracking-tight">
                        {isProcessing ? t('auth.securingConnection', 'Securing Connection') :
                         isSuccess ? t('auth.connectionSuccessful', 'Success!') :
                         t('auth.connectionFailed', 'Connection Failed')}
                    </h3>
                    <p className="text-[15px] text-neutral-500 dark:text-neutral-400 leading-relaxed font-medium">
                        {isProcessing ? 
                            t('auth.verifyingWithProvider', `Verifying your ${provider} account...`, { provider: provider.charAt(0).toUpperCase() + provider.slice(1) }) :
                         isSuccess ? 
                            t('auth.redirecting', 'Awesome! We\'re redirecting you now...') :
                         errorMessage || t('auth.socialLoginError', 'We couldn\'t verify your identity. Please try again.')}
                    </p>
                </div>
            </div>
        </motion.div>
    );
};
